import { Button, ButtonsGroup, Space, Text } from '@billy/design-system'
import React, { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { getInvoiceDocumentRouteBase } from '@features/invoices/utils/getInvoiceDocumentRouteBase'

import { useOrganizationSource, useSegment } from '../../../../../../hooks'
import { useOrganizationViewUrl } from '../../../../../../hooks/routing/useOrganizationViewUrl'
import { getQueryParamsString } from '../../../../../../utils/getQueryParamsString'
import { reactRoute } from '../../../../../../utils/routing/reactRoute'
import { USERFLOW_INVOICE_GUIDE_ID } from '../../../../constants/userflowIds'
import { widgetId } from '../../../../constants/widgetId'
import { useWidgetsActions } from '../../../../hooks/useWidgetsActions'
import { InvoicesActionPoint } from '../../enums/invoicesActionPoint'
import * as Styled from './styles'

export const OnboardingEmptyState = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const navigate = useNavigate()
  const invoiceDocumentRouteBase = getInvoiceDocumentRouteBase(InvoiceDocumentType.Invoice)
  const { url: invoicesNewUrl } = useOrganizationViewUrl(
    reactRoute.organization.invoices.getNewDocumentRoute(invoiceDocumentRouteBase),
  )
  const { trackDashboardActionPerformed } = useWidgetsActions()
  const { isSourceBilly, isSourceZervant } = useOrganizationSource()

  const handleCreateInvoiceClick = useCallback(() => {
    trackDashboardActionPerformed({ action: InvoicesActionPoint.CreateInvoiceNow, widgetId: widgetId.Invoices })

    if (isSourceBilly) {
      track('xxx - hamster - tile experiment button clicked', {
        button: 'create invoice now',
      })

      const queryParamsString = getQueryParamsString({ userflow: USERFLOW_INVOICE_GUIDE_ID })
      navigate(`${invoicesNewUrl}?${queryParamsString}`)
    }

    if (isSourceZervant && invoicesNewUrl) {
      navigate(invoicesNewUrl)
    }
  }, [invoicesNewUrl, isSourceBilly, isSourceZervant, navigate, track, trackDashboardActionPerformed])

  const handleShowHow = useCallback(() => {
    track('xxx - hamster - tile experiment button clicked', {
      button: 'show me how',
    })
    trackDashboardActionPerformed({ action: InvoicesActionPoint.ShowMeHow, widgetId: widgetId.Invoices })
    window
      .open(
        'https://help.shine.co/da/articles/609765-opret-din-forste-faktura-i-shine-regnskab?utm_source=userflow&utm_medium=internal&utm_campaign=fakturatile_show_me_how',
        '_blank',
        'noopener,noreferrer',
      )
      ?.focus()
  }, [track, trackDashboardActionPerformed])

  return (
    <Styled.WidgetContentWrapper>
      <Styled.Header>
        <Space size="s8" />
        <Text variant="display">{t('dashboard.widget.invoices.onboarding.title')}</Text>
        <Space />
        <Styled.DescriptionWrapper>
          <Text>{t('dashboard.widget.invoices.onboarding.description')}</Text>
        </Styled.DescriptionWrapper>
      </Styled.Header>
      <Styled.Footer>
        {isSourceBilly && (
          <ButtonsGroup>
            <Button onClick={handleCreateInvoiceClick} variant="filled">
              {t('dashboard.widget.invoices.onboarding.start_invoicing')}
            </Button>
            <Button variant="onColor" onClick={handleShowHow} color="warning">
              {t('dashboard.widget.invoices.onboarding.show_me_how')}
            </Button>
          </ButtonsGroup>
        )}
        {isSourceZervant && (
          <ButtonsGroup>
            <Button onClick={handleCreateInvoiceClick} variant="outlined">
              {t('dashboard.widget.invoices.onboarding.lets_begin')}
            </Button>
          </ButtonsGroup>
        )}
      </Styled.Footer>
      <Styled.Illustration />
    </Styled.WidgetContentWrapper>
  )
}
